import React, { useState } from 'react'
import { AlertCircle, RefreshCw, Play } from 'lucide-react'
import { convertToBrowserCompatible, detectVideoCodec, isCodecBrowserCompatible } from '../../utils/codecDetection'

const VideoPlaybackFallback = ({
  videoUrl,
  video,
  onConvertedVideo,
  onRetry
}) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [isConverting, setIsConverting] = useState(false)
  const [conversionProgress, setConversionProgress] = useState(0)
  const [codecInfo, setCodecInfo] = useState(null)
  const [error, setError] = useState(null)

  const analyzeVideoCodec = async () => {
    setIsAnalyzing(true)
    setError(null)

    try {
      console.log('🔍 Analyzing failed video for codec issues...')

      // Fetch the video file for analysis with correct MIME type
      const response = await fetch(videoUrl)
      const blob = await response.blob()

      // CRITICAL FIX: Create file with original MIME type from database
      // This ensures codec detection matches pre-upload behavior
      const originalMimeType = video.mime_type || video.mimeType || 'video/mp4'
      const file = new File([blob], video.filename || 'video.mp4', { type: originalMimeType })

      // Detect codec
      const codec = await detectVideoCodec(file)
      const compatibility = isCodecBrowserCompatible(codec)

      console.log('🎥 Playback failure codec analysis:', { codec, compatibility })
      setCodecInfo({ codec, compatibility })

      return { codec, compatibility, file }

    } catch (error) {
      console.error('❌ Codec analysis failed:', error)
      setError('Unable to analyze video codec')
      throw error
    } finally {
      setIsAnalyzing(false)
    }
  }

  const handleConvertVideo = async () => {
    setIsConverting(true)
    setConversionProgress(0)
    setError(null)

    try {
      // First analyze if we haven't already
      let analysisResult = codecInfo
      if (!codecInfo) {
        const result = await analyzeVideoCodec()
        analysisResult = result
      }

      if (!analysisResult?.file) {
        // Re-fetch the file for conversion
        console.log('🔄 Fetching video file for conversion...')
        const response = await fetch(videoUrl)
        const blob = await response.blob()
        const originalMimeType = video.mime_type || video.mimeType || 'video/mp4'
        analysisResult.file = new File([blob], video.filename || 'video.mp4', { type: originalMimeType })
      }

      console.log('🔄 Converting video for playback compatibility...')
      const convertedFile = await convertToBrowserCompatible(
        analysisResult.file,
        (progress) => setConversionProgress(progress)
      )

      console.log('✅ Video conversion for playback complete')

      // Create a blob URL for the converted video
      const convertedUrl = URL.createObjectURL(convertedFile)
      onConvertedVideo(convertedUrl, convertedFile)

    } catch (error) {
      console.error('❌ Video conversion for playback failed:', error)
      setError(error.message || 'Video conversion failed')
    } finally {
      setIsConverting(false)
    }
  }

  if (isConverting) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-black/80">
        <div className="text-center space-y-4 max-w-md mx-auto p-6">
          <div className="animate-spin rounded-full h-12 w-12 border-3 border-orange-500 border-t-transparent mx-auto"></div>
          <h3 className="text-lg font-semibold text-white">Converting for Playback</h3>
          <p className="text-gray-300 text-sm">
            Converting your video to a browser-compatible format for playback...
            <br/><span className="text-yellow-400 font-medium">
              ⚠️ Large videos may take considerable time. Contact admin for assistance with files over 1GB.
            </span>
          </p>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Progress</span>
              <span className="text-orange-400">{Math.round(conversionProgress)}%</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div
                className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${conversionProgress}%` }}
              ></div>
            </div>
          </div>

          <p className="text-xs text-gray-500">
            This may take a few minutes. The converted video will only be used for playback.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="text-center max-w-md mx-auto p-6">
        <AlertCircle className="w-16 h-16 mx-auto mb-4 text-orange-500" />

        <h3 className="text-xl font-bold text-white mb-2">Video Playback Issue</h3>
        <p className="text-gray-300 text-sm mb-6">
          This video may be using a codec that's not compatible with your browser.
        </p>

        {codecInfo && (
          <div className="bg-gray-800/50 rounded-lg p-4 mb-6 text-left text-sm">
            <h4 className="font-medium text-white mb-2">Video Details:</h4>
            <div className="space-y-1 text-gray-300">
              <div>Video: {codecInfo.codec.videoCodec.toUpperCase()}</div>
              <div>Audio: {codecInfo.codec.audioCodec.toUpperCase()}</div>
              <div>Resolution: {codecInfo.codec.width}x{codecInfo.codec.height}</div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-600/20 border border-red-600/30 rounded-lg p-3 mb-4 text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-3">
          {!codecInfo ? (
            <button
              onClick={analyzeVideoCodec}
              disabled={isAnalyzing}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white px-4 py-3 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {isAnalyzing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Analyzing Video...</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4" />
                  <span>Analyze Video Codec</span>
                </>
              )}
            </button>
          ) : (
            <div className="space-y-2">
              <div className="bg-red-600/20 border border-red-500/30 rounded-lg p-3 mb-2">
                <p className="text-sm text-red-300 font-medium">
                  ⚠️ WARNING: Browser conversion is VERY SLOW!
                </p>
                <p className="text-xs text-red-400 mt-1">
                  Only recommended for files under 200MB. Use external tools like Prism for larger videos.
                </p>
              </div>
              <button
                onClick={handleConvertVideo}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white px-4 py-3 rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Convert for Playback</span>
              </button>
            </div>
          )}

          <button
            onClick={onRetry}
            className="w-full bg-gray-700 hover:bg-gray-600 text-white px-4 py-3 rounded-lg transition-colors flex items-center justify-center space-x-2"
          >
            <Play className="w-4 h-4" />
            <span>Try Playing Again</span>
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-4">
          Converting only affects playback - your original video remains unchanged
        </p>
      </div>
    </div>
  )
}

export default VideoPlaybackFallback